/**
 * Project/index.js — Main project workspace.
 * Loads a project by ID from the URL, auto-saves project state to the backend
 * on every Redux change, and renders the active screen based on nav.leaf.
 * The Screens map provides dynamic screen resolution from navigation state.
 */
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import Frame from "Components/Frame/";
import MainTabs from "Components/Navigation/MainTabs";
import { HelpPanelProvider } from "Components/HelpPanel";
import WelcomeModal from "Components/WelcomeModal";
// -------------------------------------------------------------
import CheckIn from "Screens/CheckIn";
import Upload from "Screens/Upload";
import Files from "Screens/Files";
import Columns from "Screens/Columns";
import Joins from "Screens/Joins";
import Parsers from "Screens/Parsers";
import Dates from "Screens/Dates";
import Canonicals from "Screens/Canonicals";
import CheckOut from "Screens/CheckOut";
import Rollforward from "Screens/Rollforward";
import RollforwardAnalysis from "Screens/RollforwardAnalysis";
import Components from "Screens/Components";
import Master from "Screens/Master";
import Scrub from "Screens/Scrub";
import Fraud from "Screens/Fraud";
import Compare from "Screens/Compare";
import Aging from "Screens/Aging";
import AgingChart from "Screens/AgingChart";
import Filters from "Screens/Filters";
import Charts from "Screens/Charts";
import Performance from "Screens/Performance";
import Invoice from "Screens/Invoice";
import Dashboard from "Screens/Dashboard"
import Supplier from "Screens/Supplier";
import Criteria from "Screens/Criteria";
import Advance from "Screens/Advance";
import Parameters from "Screens/Parameters";
import Calculate from "Screens/Calculate";
import DocumentTypes from "Screens/DocumentTypes";
import Delinquencies from "Screens/Delinquencies";
import Dilution from "Screens/Dilution";
import LossReserve from "Screens/LossReserve";
import YieldReserve from "Screens/YieldReserve";
import AggregateReserve from "Screens/AggregateReserve";
import BuyerParent from "Screens/BuyerParent";
import RatingScale from "Screens/RatingScale";
import BuyerRatings from "Screens/BuyerRatings";
import ConcentrationLimits from "Screens/ConcentrationLimits";
import Downloads from "Screens/Downloads";
import Nerb from "Screens/Nerb";
import Report from "Screens/Report";
// -------------------------------------------------------------
import { loadProject, saveProject } from "Redux/project/actions";
import { loadChats } from "Redux/chats/actions";

// -------------------------------------------------------------
/** Maps nav.leaf string values to screen components for dynamic rendering. */
const Screens = {
  checkin: CheckIn,
  upload: Upload,
  files: Files,
  columns: Columns,
  joins: Joins,
  parsers: Parsers,
  dates: Dates,
  canonicals: Canonicals,
  checkout: CheckOut,
  components: Components,
  rollforward: Rollforward,
  rollforwardanalysis: RollforwardAnalysis,
  supplier: Supplier,
  master: Master,
  scrub: Scrub,
  fraud: Fraud,
  aging: Aging,
  agingchart: AgingChart,
  filters: Filters,
  performance: Performance,
  invoice: Invoice,
  charts: Charts,
  dashboard: Dashboard,
  criteria: Criteria,
  advance: Advance,
  parameters: Parameters,
  calculate: Calculate,
  documenttypes: DocumentTypes,
  delinquencies: Delinquencies,
  dilution: Dilution,
  lossreserve: LossReserve,
  yieldreserve: YieldReserve,
  aggregatereserve: AggregateReserve,
  buyerparent: BuyerParent,
  ratingscale: RatingScale,
  buyerratings: BuyerRatings,
  concentrationlimits: ConcentrationLimits,
  nerb: Nerb,
  report: Report,
  downloads: Downloads,
  compare: Compare,
};

// Screens that always show ORIGINAL-project data (not scenario-scoped). While a
// scenario is active they'd display base numbers under the scenario banner —
// misleading — so they're guarded with a "switch back to Original" notice.
const BASE_ONLY = new Set([
  "report", "downloads", "fraud", "scrub", "charts", "rollforwardanalysis", "master",
]);

// -------------------------------------------------------------
// -------------------------------------------------------------
const Index = () => {
  const dispatch = useDispatch();
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const { pid } = useParams();
  // Load project from backend on mount. Once loaded, set the navigation mode
  // (pre-analysis ingestion flow vs post-analysis review flow).
  useEffect(() => {
    dispatch(loadProject(pid, (project) => {
      const whichView = project.hasAnalysis ? "post" : "pre";
      dispatch({type: "SET_PRE_OR_POST", preOrPost: whichView, projectType: project.projectType || "receivables"});
      dispatch(loadChats());
      setHasLoaded(true);
      // Show welcome modal if user hasn't dismissed it for this project
      const dismissed = localStorage.getItem(`ev-welcome-${pid}`);
      if (!dismissed) setShowWelcome(true);
    }));
  }, [pid, dispatch]);

  const project = useSelector((state) => state.project);

  // Auto-save: persist project state to the backend whenever the Redux
  // project slice changes. Guarded by hasLoaded to avoid saving the
  // initial empty state before the real project data arrives.
  useEffect(() => {
    if(hasLoaded) {
      dispatch(saveProject());
    }
  }, [project, dispatch, hasLoaded]);

  const nav = useSelector((state) => state.nav);
  const { leaf, initialized } = nav;
  const activeScenario = useSelector((state) => state.data.activeScenario) || "base";

  if (!project.id || !initialized) return (
    <Frame>
        Loading...
    </Frame>
  );
  

  // Dynamically resolve the screen component from the nav leaf name.
  // Falls back to an empty div if the leaf doesn't match any known screen.
  const inScenario = activeScenario !== "base";
  const ActiveScreen =
    inScenario && BASE_ONLY.has(leaf)
      ? () => (
          <div className="notification is-warning is-light">
            This screen shows the <strong>original project</strong>, not scenario data.
            Switch back to <strong>Original</strong> in the top bar to use it.
          </div>
        )
      : Screens.hasOwnProperty(leaf)
      ? Screens[leaf]
      : () => <div></div>;

  return (
    <Frame>
      <HelpPanelProvider>
        <MainTabs>
          {/* key forces a remount per tab so the screen glides in */}
          <div key={leaf} className="screen-enter">
            <ActiveScreen />
          </div>
        </MainTabs>
      </HelpPanelProvider>
      {showWelcome && (
        <WelcomeModal onStart={() => {
          setShowWelcome(false);
          localStorage.setItem(`ev-welcome-${pid}`, "1");
        }} />
      )}
    </Frame>
  );
};

export default Index;
